import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetAirlines from "../../hooks/airlines/useGetAirlines";
import useDeleteAirline from "../../hooks/airlines/useDeleteAirline";
import useDeleteAllAirlines from "../../hooks/airlines/useDeleteAllAirlines";
import useRestoreAirline from "../../hooks/airlines/useRestoreAirline";
import useRestoreAllAirlines from "../../hooks/airlines/useRestoreAllAirlines";
import useForceDeleteAirline from "../../hooks/airlines/useForceDeleteAirline";
import useForceDeleteAllAirlines from "../../hooks/airlines/useForceDeleteAllAirlines";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import AirlinesTableRow from "./Table/AirlinesTableRow";
import AirlinesTableColumns from "./Table/AirlinesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const AirlinesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("airlines_list"),
            create: has("airlines_create"),
            edit: has("airlines_edit"),
            delete: has("airlines_delete"),
            forceDelete: has("airlines_force_delete"),
            archiveList: has("airlines_archive_list"),
            restore: has("airlines_restore"),
            restoreAll: has("airlines_restore-all-table"),
            deleteAll: has("airlines_delete-all-table"),
            massForceDeleteAll: has("airlines_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/airlines/archive");

    // CRUD Hooks
    const { deleteAirline, isDelete } = useDeleteAirline();
    const { deleteAllAirlines, isDeleteAll } = useDeleteAllAirlines();
    const { restoreAirline, isRestore } = useRestoreAirline();
    const { restoreAllAirlines, isRestoreAll } = useRestoreAllAirlines();
    const { forceDeleteAirline, isForceDelete } = useForceDeleteAirline();
    const { forceDeleteAllAirlines, isForceDeleteAll } = useForceDeleteAllAirlines();

    // Table Actions Hook
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/airlines",
        deleteSingle: deleteAirline,
        deleteAll: deleteAllAirlines,
        restoreSingle: restoreAirline,
        restoreAll: restoreAllAirlines,
        forceDeleteSingle: forceDeleteAirline,
        forceDeleteAll: forceDeleteAllAirlines,
        isArchive,
    });

    // Local Filters
    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, airlines, meta } = useGetAirlines(page, perPage, filters);
    const items = useMemo(() => airlines || [], [airlines]);

    const rowMenuDisabled = isArchive ? (!perms.restore && !perms.forceDelete) : (!perms.edit && !perms.delete);

    const renderRow = (params) => (
        <AirlinesTableRow
            key={params.item.id}
            airline={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "شركات الطيران" : "Airlines";
    const subtitle = isArchive
        ? isArabic
            ? "عرض شركات الطيران المحذوفة (الأرشيف)"
            : "Archived airlines (soft deleted)"
        : isArabic
            ? "إدارة شركات الطيران المتاحة في النظام"
            : "Manage available airlines in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة شركة طيران"
            : "Create new airline";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission");
                return;
            }
            if (isRestoreAll) return;
            if (!selectedIds.length) {
                toast.error(isArabic ? "الرجاء تحديد العناصر المراد استرجاعها أولاً" : "Please select items to restore first");
                return;
            }
            restoreAllAirlines(selectedIds);
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission");
                return;
            }
            navigate("/airlines/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/airlines/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/airlines"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={AirlinesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية لتنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا يوجد شركات طيران في الأرشيف" : "لا يوجد شركات طيران"}
                emptyLabelEn={isArchive ? "No archived airlines" : "No airlines"}
                loadErrorAr="حدث خطاء اثناء تحميل شركات الطيران"
                loadErrorEn="Failed to load airlines"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default AirlinesTable;
